import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Search, Upload, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/errorHandler';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contacts = () => {
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [studentsUploadDialogOpen, setStudentsUploadDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingStudents, setUploadingStudents] = useState(false);
  const fileInputRef = useRef(null);
  const studentsFileInputRef = useRef(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalContacts, setTotalContacts] = useState(0);
  const [perPage] = useState(50);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    profile_photo_url: '',
    categoria: 'não_aluno',
  });

  const [searchTerm, setSearchTerm] = useState(''); // Temporary search input
  const [activeSearch, setActiveSearch] = useState(''); // Active search query
  
  // Category filter state
  const [categoryFilter, setCategoryFilter] = useState(null); // null = todos
  const [categoryStats, setCategoryStats] = useState({
    não_aluno: 0,
    aluno: 0,
    colaborador: 0,
    profissional: 0,
    total: 0
  });

  useEffect(() => {
    fetchContacts();
    fetchCategoryStats();
  }, [currentPage, activeSearch, categoryFilter]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = { 
        page: currentPage, 
        per_page: perPage 
      };
      
      // Add search parameter if search is active
      if (activeSearch) {
        params.search = activeSearch;
      }
      
      // Add category filter if selected
      if (categoryFilter) {
        params.categoria = categoryFilter;
      }
      
      const response = await axios.get(`${API}/contacts`, { params });
      setContacts(response.data.contacts);
      setTotalPages(response.data.total_pages);
      setTotalContacts(response.data.total);
    } catch (error) {
      toast.error('Erro ao carregar contatos');
    }
    setLoading(false);
  };
  
  const fetchCategoryStats = async () => {
    try {
      const response = await axios.get(`${API}/contacts/stats/by-category`);
      setCategoryStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas de categoria:', error);
    }
  };

  const handleSearch = () => {
    setActiveSearch(searchTerm);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setActiveSearch('');
    setCurrentPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, formData);
        toast.success('Contato atualizado!');
      } else {
        await axios.post(`${API}/contacts`, formData);
        toast.success('Contato criado!');
      }
      fetchContacts();
      fetchCategoryStats(); // Refresh category statistics
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error('Erro ao salvar contato');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir este contato?')) return;
    try {
      await axios.delete(`${API}/contacts/${id}`);
      toast.success('Contato excluído!');
      fetchContacts();
      fetchCategoryStats(); // Refresh category statistics
    } catch (error) {
      toast.error('Erro ao excluir contato');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const text = await file.text();
      const contactsData = JSON.parse(text);
      
      const response = await axios.post(`${API}/contacts/upload`, {
        contacts: contactsData
      });
      
      toast.success(response.data.message);
      fetchContacts();
      fetchCategoryStats(); // Refresh category statistics
      setUploadDialogOpen(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || 'Erro ao importar contatos');
    }
    setUploading(false);
  };

  const handleStudentsFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.txt')) {
      toast.error('Por favor, selecione um arquivo .txt');
      return;
    }

    setUploadingStudents(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/contacts/mark-students`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      // Show detailed results
      const { total_in_file, found_and_converted, not_found } = response.data;
      
      toast.success(
        `✅ Processamento concluído!\n` +
        `📄 Total no arquivo: ${total_in_file}\n` +
        `✅ Encontrados e convertidos: ${found_and_converted}\n` +
        `❌ Não encontrados: ${not_found}`,
        { duration: 8000 }
      );
      
      fetchContacts();
      fetchCategoryStats(); // Refresh category statistics
      setStudentsUploadDialogOpen(false);
      if (studentsFileInputRef.current) {
        studentsFileInputRef.current.value = '';
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage || 'Erro ao processar arquivo de alunos');
    }
    setUploadingStudents(false);
  };

  const resetForm = () => {
    setFormData({ name: '', phone: '', profile_photo_url: '', categoria: 'não_aluno' });
    setEditingContact(null);
  };

  const openEditDialog = (contact) => {
    setEditingContact(contact);
    setFormData({
      name: contact.name,
      phone: contact.phone,
      profile_photo_url: contact.profile_photo_url || '',
      categoria: contact.categoria || 'não_aluno',
    });
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div data-testid="contacts-page">
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Contatos</h1>
          <p className="text-gray-600">Gerencie seus contatos do WhatsApp</p>
        </div>

        <div className="flex gap-2">
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="upload-contacts-button"
                variant="outline"
                className="border-emerald-700 text-emerald-700 hover:bg-emerald-50"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Importar Contatos</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Faça upload de um arquivo JSON com a estrutura:
                </p>
                <pre className="p-3 bg-gray-100 rounded text-xs overflow-x-auto">
{`[
  {
    "contact_name": "Nome",
    "jid": "5511999887766@s.whatsapp.net"
  }
]`}
                </pre>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  disabled={uploading}
                  data-testid="file-upload-input"
                />
                {uploading && (
                  <p className="text-sm text-emerald-700">Importando contatos...</p>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={studentsUploadDialogOpen} onOpenChange={setStudentsUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button
                data-testid="mark-students-button"
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <Upload className="w-4 h-4 mr-2" />
                Marcar Alunos
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Marcar Contatos como Alunos</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900 font-medium mb-2">
                    📋 Instruções:
                  </p>
                  <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                    <li>Arquivo deve ser em formato <strong>.txt</strong></li>
                    <li>Um número de telefone por linha</li>
                    <li>Formato: +5511999887766 ou 5511999887766</li>
                    <li>Contatos encontrados serão marcados como alunos</li>
                  </ul>
                </div>
                
                <div className="p-3 bg-gray-50 rounded border border-gray-200">
                  <p className="text-xs text-gray-600 mb-1">Exemplo de arquivo:</p>
                  <pre className="text-xs text-gray-700">
{`+5511999887766
5511988776655
5511977665544`}
                  </pre>
                </div>

                <Input
                  ref={studentsFileInputRef}
                  type="file"
                  accept=".txt"
                  onChange={handleStudentsFileUpload}
                  disabled={uploadingStudents}
                  data-testid="students-file-upload-input"
                />
                
                {uploadingStudents && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-600"></div>
                    <p className="text-sm text-blue-700">Processando arquivo...</p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button
                data-testid="add-contact-button"
                className="bg-gradient-to-r from-emerald-700 to-green-400 hover:from-emerald-900 hover:to-green-500 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Novo Contato
              </Button>
            </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingContact ? 'Editar Contato' : 'Novo Contato'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="name">Nome</Label>
                <Input
                  id="name"
                  data-testid="contact-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  id="phone"
                  data-testid="contact-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+5511999887766"
                  required
                />
              </div>
              <div>
                <Label htmlFor="photo">URL da Foto (opcional)</Label>
                <Input
                  id="photo"
                  data-testid="contact-photo-input"
                  value={formData.profile_photo_url}
                  onChange={(e) => setFormData({ ...formData, profile_photo_url: e.target.value })}
                  placeholder="https://..."
                />
              </div>
              <div>
                <Label>Categoria</Label>
                <Select 
                  value={formData.categoria} 
                  onValueChange={(value) => setFormData({ ...formData, categoria: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione a categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="não_aluno">👤 Não Aluno</SelectItem>
                    <SelectItem value="aluno">🎓 Aluno</SelectItem>
                    <SelectItem value="colaborador">💼 Colaborador</SelectItem>
                    <SelectItem value="profissional">👔 Profissional</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                data-testid="contact-submit-button"
                type="submit"
                className="w-full bg-gradient-to-r from-emerald-700 to-green-400 hover:from-emerald-900 hover:to-green-500 text-white"
              >
                {editingContact ? 'Atualizar' : 'Criar'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Category Filter Buttons */}
      <div className="mb-6">
        <div className="flex flex-wrap gap-2">
          <Button
            variant={categoryFilter === null ? "default" : "outline"}
            onClick={() => {
              setCategoryFilter(null);
              setCurrentPage(1);
            }}
            className={categoryFilter === null 
              ? "bg-gradient-to-r from-emerald-700 to-green-400 text-white" 
              : "border-gray-300 hover:bg-gray-50"
            }
          >
            📋 Todos
            <span className="ml-2 px-2 py-0.5 rounded-full bg-white/20 text-xs font-semibold">
              {categoryStats.total}
            </span>
          </Button>
          
          <Button
            variant={categoryFilter === 'aluno' ? "default" : "outline"}
            onClick={() => {
              setCategoryFilter('aluno');
              setCurrentPage(1);
            }}
            className={categoryFilter === 'aluno'
              ? "bg-gradient-to-r from-blue-600 to-blue-400 text-white" 
              : "border-blue-300 text-blue-700 hover:bg-blue-50"
            }
          >
            🎓 Alunos
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
              categoryFilter === 'aluno' ? 'bg-white/20' : 'bg-blue-100'
            }`}>
              {categoryStats.aluno}
            </span>
          </Button>
          
          <Button
            variant={categoryFilter === 'não_aluno' ? "default" : "outline"}
            onClick={() => {
              setCategoryFilter('não_aluno');
              setCurrentPage(1);
            }}
            className={categoryFilter === 'não_aluno'
              ? "bg-gradient-to-r from-gray-600 to-gray-400 text-white" 
              : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }
          >
            👤 Não Alunos
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
              categoryFilter === 'não_aluno' ? 'bg-white/20' : 'bg-gray-100'
            }`}>
              {categoryStats.não_aluno}
            </span>
          </Button>
          
          <Button
            variant={categoryFilter === 'colaborador' ? "default" : "outline"}
            onClick={() => {
              setCategoryFilter('colaborador');
              setCurrentPage(1);
            }}
            className={categoryFilter === 'colaborador'
              ? "bg-gradient-to-r from-purple-600 to-purple-400 text-white" 
              : "border-purple-300 text-purple-700 hover:bg-purple-50"
            }
          >
            💼 Colaboradores
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
              categoryFilter === 'colaborador' ? 'bg-white/20' : 'bg-purple-100'
            }`}>
              {categoryStats.colaborador}
            </span>
          </Button>
          
          <Button
            variant={categoryFilter === 'profissional' ? "default" : "outline"}
            onClick={() => {
              setCategoryFilter('profissional');
              setCurrentPage(1);
            }}
            className={categoryFilter === 'profissional'
              ? "bg-gradient-to-r from-teal-600 to-teal-400 text-white" 
              : "border-teal-300 text-teal-700 hover:bg-teal-50"
            }
          >
            👔 Profissionais
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-semibold ${
              categoryFilter === 'profissional' ? 'bg-white/20' : 'bg-teal-100'
            }`}>
              {categoryStats.profissional}
            </span>
          </Button>
        </div>
        
        {categoryFilter && (
          <p className="text-sm text-emerald-700 mt-2">
            🔍 Filtrando por: <strong>{
              categoryFilter === 'aluno' ? 'Alunos' :
              categoryFilter === 'não_aluno' ? 'Não Alunos' :
              categoryFilter === 'colaborador' ? 'Colaboradores' :
              'Profissionais'
            }</strong> - {totalContacts} contato(s)
          </p>
        )}
      </div>

      <div className="mb-6">
        <div className="relative flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              data-testid="contact-search-input"
              placeholder="Digite para buscar contatos em toda a base..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={handleKeyPress}
              className="pl-10"
            />
          </div>
          <Button
            data-testid="search-button"
            onClick={handleSearch}
            disabled={loading}
            className="bg-emerald-700 hover:bg-emerald-800 text-white"
          >
            <Search className="w-4 h-4 mr-2" />
            Buscar
          </Button>
          {activeSearch && (
            <Button
              data-testid="clear-search-button"
              variant="outline"
              onClick={handleClearSearch}
              disabled={loading}
              className="border-gray-300 hover:bg-gray-100"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Limpar
            </Button>
          )}
        </div>
        {activeSearch && (
          <p className="text-sm text-emerald-700 mt-2">
            🔍 Buscando por: "<strong>{activeSearch}</strong>" - {totalContacts} resultado(s)
          </p>
        )}
      </div>

      {/* Stats */}
      <div className="mb-4 flex justify-between items-center">
        <p className="text-sm text-gray-600">
          Exibindo {contacts.length} de {totalContacts} contatos
        </p>
        <p className="text-sm text-gray-600">
          Página {currentPage} de {totalPages}
        </p>
      </div>

      {contacts.length === 0 ? (
        <Card className="border-0 shadow-lg">
          <CardContent className="p-12 text-center">
            <p className="text-gray-500">Nenhum contato encontrado</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Tabela de Contatos */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Contato
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Telefone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Categoria
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {contacts.map((contact) => (
                    <tr key={contact.id} data-testid="contact-row" className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarImage src={contact.profile_photo_url} />
                            <AvatarFallback className="bg-emerald-100 text-emerald-700">
                              {contact.name[0]?.toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-600">{contact.phone}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                          {contact.categoria === 'aluno' && '🎓 Aluno'}
                          {contact.categoria === 'não_aluno' && '👤 Não Aluno'}
                          {contact.categoria === 'colaborador' && '💼 Colaborador'}
                          {contact.categoria === 'profissional' && '👔 Profissional'}
                          {!contact.categoria && '👤 Não Aluno'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center gap-2">
                          <Button
                            data-testid="edit-contact-button"
                            variant="ghost"
                            size="icon"
                            onClick={() => openEditDialog(contact)}
                            className="hover:bg-emerald-50"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid="delete-contact-button"
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(contact.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="mt-8 flex justify-center items-center gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1 || loading}
              className="border-emerald-700 text-emerald-700 hover:bg-emerald-50"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>
            
            <div className="flex gap-2">
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                // Show first, last, current, and adjacent pages
                if (
                  page === 1 || 
                  page === totalPages || 
                  (page >= currentPage - 1 && page <= currentPage + 1)
                ) {
                  return (
                    <Button
                      key={page}
                      variant={page === currentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      disabled={loading}
                      className={page === currentPage 
                        ? "bg-emerald-700 text-white" 
                        : "border-emerald-700 text-emerald-700 hover:bg-emerald-50"
                      }
                    >
                      {page}
                    </Button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="px-2">...</span>;
                }
                return null;
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages || loading}
              className="border-emerald-700 text-emerald-700 hover:bg-emerald-50"
            >
              Próximo
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default Contacts;
